import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TripleSpinner } from "../ui/TripleSpinner";
import { acceptPendingHandoff, getPendingHandoffToken, rejectPendingHandoff } from "./tokenHandoff";

interface HandoffProfile {
  email?: string;
  firstName?: string;
  lastName?: string;
}

/** How long to wait for the profile before giving up and treating the hand-off as refused. */
const PROFILE_TIMEOUT_MS = 8000;

/** Who the pending token belongs to, in the plainest terms available. */
function identify(profile: HandoffProfile): string {
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim();
  return profile.email || name;
}

/**
 * Asks whose account a hand-off token belongs to before adopting it.
 *
 * Only reached when the token arrived without a trusted referrer — see the session-fixation
 * note in tokenHandoff.ts. Naming the account is the whole point: someone who followed a
 * link from an attacker sees an email that isn't theirs and can back out, where a silent
 * sign-in would have left them entering their address into a stranger's checkout.
 */
function HandoffConfirm({ token, onAccept, onReject }: { token: string; onAccept: () => void; onReject: () => void }) {
  const { t, ready } = useTranslation();
  const [identity, setIdentity] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let active = true;
    const base = process.env.REACT_APP_BASE_API_URL || "api/v1/";
    const controller = new AbortController();
    // This gate stands in front of the entire app, so a request that never settles would
    // leave a spinner where the store should be. Give up and treat it as a rejected
    // hand-off: the user lands on login, which they can act on.
    const timer = window.setTimeout(() => controller.abort(), PROFILE_TIMEOUT_MS);

    fetch(`${base}me`, {
      headers: { Authorization: token, Accept: "application/json" },
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((profile: HandoffProfile) => {
        window.clearTimeout(timer);
        if (!active) return;
        setIdentity(identify(profile));
        setChecking(false);
      })
      .catch(() => {
        window.clearTimeout(timer);
        // The token was rejected by the API, the call failed outright, or it timed out.
        // Either way there's nothing to confirm and nothing worth showing — drop it and let
        // the app boot signed out, which lands the user on login. The `active` check keeps
        // the abort fired by cleanup below from re-entering this after the user has already
        // chosen.
        if (active) onReject();
      });

    return () => {
      active = false;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [token, onReject]);

  if (checking || !ready) {
    return (
      <div className="vh-100 d-flex flex-column align-items-center justify-content-center">
        <TripleSpinner />
      </div>
    );
  }

  return (
    <div className="bits-app">
      <div className="vh-100 d-flex flex-column align-items-center justify-content-center text-center px-4">
        <div aria-hidden="true" className="ds-glow" />
        <h1>{identity ? t("auth.handoff.title", { identity }) : t("auth.handoff.titleUnknown")}</h1>
        <p className="mt-3" style={{ maxWidth: "34rem" }}>
          {t("auth.handoff.body")}
        </p>
        <div className="mt-4 d-flex flex-column flex-sm-row gap-3">
          <button type="button" className="ds-btn ds-btn--ink" onClick={onAccept}>
            <span>{t("auth.handoff.confirm")}</span>
          </button>
          <button type="button" className="ds-btn ds-btn--outline" onClick={onReject}>
            <span>{t("auth.handoff.reject")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Holds the app back while a hand-off token is confirmed. With nothing pending — the normal
 * case, and every case that isn't a hand-off — it renders the app untouched.
 */
export function HandoffGate({ children }: { children: JSX.Element }) {
  // A plain read, not a consuming one: StrictMode invokes this initialiser twice in dev.
  const [pending, setPending] = useState<string | null>(getPendingHandoffToken);

  // Stable identities: onReject is a dependency of the profile request below, and a fresh
  // arrow on every render would re-run it.
  const handleAccept = useCallback(() => {
    acceptPendingHandoff();
    setPending(null);
  }, []);
  const handleReject = useCallback(() => {
    rejectPendingHandoff();
    setPending(null);
  }, []);

  if (!pending) {
    return children;
  }
  return <HandoffConfirm token={pending} onAccept={handleAccept} onReject={handleReject} />;
}
